import styled from "styled-components";
import { ReportDatabaseContext } from "../../../Contexts/ReportDatabaseContext";
import { useState, useContext, useEffect } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { generateDateArray } from "../../../Helpers/TimeFunctions";
import {
  getDashBoardData,
  calculateTotalHours,
} from "../../../Helpers/ReportDataFunctions";

const IndividualDashboardWrapper = styled.div`
  width: 100%;
  padding: 0 10px;
  display: grid;
  grid-template-areas: "areas areas" "time-share in-person";
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media screen and (max-width: 500px) {
    grid-template-areas: "areas" "time-share" "in-person";
    grid-template-columns: 1fr;
    width: 100%;
    .bar-chart {
      width: 90vw;
    }
  }
  .time-share {
    grid-area: time-share;
  }
  .in-person {
    grid-area: in-person;
  }
  .donut-chart {
    min-height: 70vw;
  }
  .bar-chart {
    width: 100%;
  }
  .areas {
    grid-area: areas;
    width: 100%;
  }
`;

const GraphCard = styled.div`
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 0px 15px 3px rgba(197, 197, 197, 0.201);

  h2 {
    text-transform: capitalize;
    margin-bottom: 10px;
    font-size: 15px;
  }
  p {
    align-self: flex-start;
    font-size: 12px;
    font-weight: 300;
  }
`;

export default function IndividualDashboard({ dates, selectedNodeId }: Props) {
  const [timeShareSeries, setTimeShareSeries] = useState<{ name: string, data: number[] }[]>([
    { name: "Hours", data: [0] },
  ]);
  const [inPersonSeries, setInPersonSeries] = useState<number[]>([0, 0]);
  const reportContext = useContext(ReportDatabaseContext);
  const reportData = reportContext.reportData;
  const [reportsMatchingUserAndDate, setReportsMatchingUserAndDate] = useState<
    ReportData[]
  >([]);
  const [barChartsSeries, setBarChartsSeries] = useState<any>([]);

  /*   useEffect(() => {
    console.log("DateArray", generateDateArray(dates[0], dates[1]));
    console.log("barChartsSeries", barChartsSeries);
  }, [barChartsSeries]); */

  //set matching reports
  useEffect(() => {
    if (reportData && selectedNodeId && dates[0] && dates[1]) {
      const reportMatch = reportData.filter((report) => {
        const dateRangeArray = generateDateArray(dates[0], dates[1]);
        const firstDay = new Date(dateRangeArray[0].startDate);
        const lastDay = new Date(
          dateRangeArray[dateRangeArray.length - 1].endDate
        );
        return (
          report.userId === selectedNodeId &&
          new Date(report.reportPeriodDate) >= firstDay &&
          new Date(report.reportPeriodDate) <= lastDay
        );
      });
      setReportsMatchingUserAndDate(reportMatch);
    }
  }, [reportData, selectedNodeId, dates]);

  //extract data from matching reports
  useEffect(() => {
    if (
      reportsMatchingUserAndDate &&
      careAreasDatescategories &&
      dates[1] &&
      dates[0]
    ) {
      const individualData = getDashBoardData(
        dates,
        reportsMatchingUserAndDate
      );

      setInPersonSeries([
        individualData.totalInPersonHours,
        individualData.totalWrittenHours,
      ]);

      setTimeShareSeries([
        {
          name: "Hours",
          data: [
            individualData.totalHours,
            individualData.adminAndMeetingHours,
            individualData.personalDevelopment,
          ],
        },
      ]);

      const seriesFromReport = [
        {
          name: "Crisis",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["crisisHours"]
          ),
        },
        {
          name: "Relationships",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["relationshipHours"]
          ),
        },
        {
          name: "Life Skills",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["lifeSkillsHours"]
          ),
        },
        {
          name: "Trauma",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["traumaHours"]
          ),
        },
        {
          name: "Additional Needs",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["addNeedsHours"]
          ),
        },
        {
          name: "Health",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["healthHours"]
          ),
        },
        {
          name: "Mental Health",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["mentalHealthHours"]
          ),
        },
      ];
      setBarChartsSeries(seriesFromReport);
    }
  }, [reportsMatchingUserAndDate]);

  const timeShareOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "80%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [
        ["Pastoral", "Care"],
        ["Admin", "& Meetings"],
        ["Personal", "Developement"],
      ],
      labels: {
        show: false,
      }
    },
  };

  const inPersonShareOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    legend: {
      position: "bottom",
    },
    labels: ["Face to Face & Calls", "Text & Email"],
  };

  const careAreasDatescategories = generateDateArray(dates[0], dates[1]).map(
    (dateRange) =>
      dateRange.startDate.toLocaleDateString("en-UK", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
  );

  const careAreasOptions: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "100%",
      toolbar: {
        show: false,
      },

      offsetY: 0,
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 500,
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: ["#ffffff"],
      },
    },
    colors: [
      "var(--crisis-color)",
      "var(--relationships-color)",
      "var(--lifeSkills-color)",
      "var(--trauma-color)",
      "var(--additionalNeeds-color)",
      "var(--health-color)",
      "var(--mentalHealth-color)",
    ],
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "90%",
      },
    },
    xaxis: {
      categories: careAreasDatescategories,
    },
  };

  return (
    <IndividualDashboardWrapper>
      <GraphCard className="in-person">
        <h2>In Person Time</h2>
        <Chart
          className="donut-chart"
          options={inPersonShareOptions}
          series={inPersonSeries}
          type="donut"
          height={260}
        />
      </GraphCard>
      <GraphCard className="time-share">
        <h2>Time Share</h2>
        <Chart
          className="donut-chart"
          options={timeShareOptions}
          series={timeShareSeries}
          type="bar"
          height={260}
        />
      </GraphCard>
      <GraphCard className="areas">
        <h2>Pastoral Care Areas</h2>
        <Chart
          className="bar-chart"
          options={careAreasOptions}
          series={barChartsSeries}
          height={70 + 60 * careAreasDatescategories.length}
          type="bar"
        />
      </GraphCard>
    </IndividualDashboardWrapper>
  );
}

interface Props {
  dates: Date[];
  selectedNodeId: string | undefined;
}

/* Individual
Line chart: see appointments in each area of the wheel
Pie chart: Showing time spent on admin, meetings and pastoral appointments
Column total hours each month */
